import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import {
  addToCartEventData,
  trackProductClick,
  buildItemTrackingData,
  setLocalStorageProperties,
} from 'lib/tracking';
import datalayer from 'lib/datalayer';
import cloneDeep from 'lodash.clonedeep';
import {
  isTablet,
  decode,
  slug,
  formatPrice,
  cleanseProductJson,
  getStarsHTML,
  convertGQLToProductJson,
  htmlDecode,
  checkBundleAvailability,
  checkProductAvailability,
} from 'utils/general';

import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import productBundle from 'mixins/product-bundle';
import select from 'dom-select';
import { generateSrcset } from '../../assets/js/utils/general';
import { trackEvent } from '../../assets/js/lib/tracking';
import Vue from 'vue';

Vue.config.ignoredElements = [
  'rivo-favorite-button',
];

/**
 * This component is used throughout the site,
 * in product carousels and on the PLP page.
 */
export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    'inline-quick-view': () =>
      import('../inline-quick-view/inline-quick-view.vue'),
    'transition-expand': () =>
      import('../transition-expand/transition-expand.vue'),
    'product-card-price-details': () =>
      import('./product-card-price-details.vue'),
    'product-card-cta-actions': () => import('./product-card-cta-actions.vue'),
  },
  mixins: [productBundle],
  props: [
    'customImage',
    'product',
    'decode',
    'disableQuickShop',
    'parentTitle',
    'variantId',
    'sectionName',
    'listType',
    'gwp',
    'stepKey',
    'cardType',
    'data',
    'additionalClasses',
    'cardIndex',
    'collectionAwards',
    'inlineCard',
    'gwpCollectionProduct',
  ],
  /**
   * Data can come via a JSON string that needs to be
   * decoded, or via an javascript object, passed by a
   * parent component or a Vuex store.
   */
  data() {
    let product;
    const gwpRawData = this.data ? JSON.parse(this.data) : null;
    const gwpType = gwpRawData ? gwpRawData.type : null;

    if (!!this.decode && this.product) {
      product = JSON.parse(decode(this.product));
      product = cleanseProductJson(product);
    } else {
      product = this.product || {
        title: '',
        price: '',
        featuredImage: '',
        variants: [],
        optionNames: [],
        optionsWithValues: {},
        reviews: {},
      };

      product = cleanseProductJson(product);
    }

    return {
      ...product,
      activeOption: '',
      selectedColor: false,
      inlineQuickViewOpen: false,
      inlineSwatches: false,
      inlineProduct: {},
      shadeSelected: false,
      currentShade: null,
      optionMap: { Size: [] },
      hasMultiSwatch: false,
      inCustomSet: this.listType === 'Custom Set',
      isVTO: this.cardType === 'vto',
      isRecommended: !!product.isRecommended,
      gwpType: gwpType,
      isTablet: isTablet(),
      isCartRecommendation: this.listType === 'Header cart',
      addingToCart: false,
      limitReached: false,
      restrictedUnavailable: false,
      restrictedUnavailableProduct: false,
      isBundle: false,
      currentVariantId: this.variantId,
    };
  },
  async mounted() {
    this.activeOption = this.firstOption;
    if (this.swatches) {
      this.selectedColor = slug(this.swatches[0]);
    }
    this.buildOptionMap();

    const bundleProductHandles =
      this.productObject.bundled_products ||
      this.productObject?.metafields?.accentuate?.bundled_products ||
      this.productObject.bundledProducts; // cleanse product

    if (
      (bundleProductHandles && bundleProductHandles.length) ||
      (this.productObject.type === 'Bundle Builder' &&
        bundleProductHandles &&
        bundleProductHandles.length)
    ) {
      this.isBundle = true;
      await this.fetchBundleProductsByHandles(bundleProductHandles);

      const productSlugs = bundleProductHandles[0].split('|');
      if (productSlugs.length > 1) {
        this.shadeCount = productSlugs.length;
      }

      let slugs = [];
      let multiSwatchesCount = 0;
      bundleProductHandles.forEach((slug, i) => {
        slugs = slug.split('|');
        if (slugs.length > 1) {
          multiSwatchesCount++;
        }
      });

      if (multiSwatchesCount >= 1) {
        this.hasMultiSwatch = true;
      }
    }

    if (this.available && !this.isBundle) {
      this.restrictedUnavailableProduct = await this.checkIsProductAvailable();
    }

    if (this.shadeCount > 1 && !this.isBundle) {
      if (this.isCartRecommendation || this.isGWP || this.inlineCard) {
        this.inlineProduct = this.product;
        this.inlineSwatches = true;
      }

      const productCardLoadedEvent = new CustomEvent('product-card:loaded');
      window.dispatchEvent(productCardLoadedEvent);

      this.onResize();
    }

    if (this.product.isRecent) {
      this.price = this.product.variants[0].price;
    }

    this.$on('inline-quick-view:shade-selected', this.handleShadeSelected);
    this.$on('inline-quick-view:close-quick-view', this.closeInlineQuickView);
    this.$on('inline-quick-view:open-quick-view', this.openInlineQuickView);

    this._initRivoMutationObserver();
  },
  created() {
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  computed: {
    ...mapState('global', ['cart']),
    ...mapGetters('global', [
      'rewardUserList',
      'userSelectionReady',
      'rewardUserProductsLeft',
      'replaceProducts',
      'gwpThresholds',
      'totalCartGWPAmount',
    ]),
    ...mapState('product', ['customSelected']),
    productObject() {
      let decodedProduct;
      if (typeof this.product === 'string') {
        decodedProduct = JSON.parse(decode(this.product));
        decodedProduct = cleanseProductJson(decodedProduct);
      }
      return decodedProduct || this.product;
    },
    isDonateProduct() {
      return typeof this.tags !== 'undefined' && this.tags
        ? this.tags.includes('donation')
        : false;
    },
    isGiftCard() {
      return typeof this.tags !== 'undefined' && this.tags
        ? this.tags.includes('gift card')
        : false;
    },
    finalTitle() {
      return this.replaceProductData
        ? this.replaceProductData.find((p) => {
            return p.id === parseInt(this.variants[0].id);
          })
        : false;
    },
    getSelectedVariant() {
      return this.variants.find((variant) => {
        return parseInt(variant.id) === parseInt(this.currentVariantId)
      }) || this.variants[0]
    },
    formattedTitle() {
      // return 'Hydra Vizor Invisible Moisturizer Broad Spectrum Spf 15 Sunscreen Refill '
      if (this.variantId) {
        const variantTitle = this.getSelectedVariant?.title;
        if (variantTitle) {
          return this.title.split(window.__GLOBAL__.productDelimiter)[0] + ' ' + variantTitle;
        }
      }
      return this.title.split(window.__GLOBAL__.productDelimiter)[0];
    },
    variantTitle() {
      if (this.product.isRecent && this.product.variants?.length > 1) {
        return this.product.variants[0].title;
      }
      return this.title.split(window.__GLOBAL__.productDelimiter)[1];
    },
    stars() {
      if (!this.product.reviewsAverage) return '';
      return getStarsHTML(this.product.reviewsAverage);
    },
    productReviewsCount() {
      return this.product.reviewsCount || 0;
    },
    reviewsText() {
      if (!this.productReviewsCount) return '';
      return `(${this.productReviewsCount})`;
    },
    starsAriaLabel() {
      if (!this.product.reviewsAverage) return '';
      return `${this.product.reviewsAverage} out of 5 stars`;
    },
    totalReviews() {
      return this.reviewsText;
    },
    formattedPrice() {
      if (this.isDonateProduct || this.isGiftCard) {
        const low = this.variants[0]?.price;
        const high = this.variants[this.variants.length - 1]?.price;
        return `${formatPrice(low, true)} - ${formatPrice(high, true)}`;
      }

      const variant = this.getSelectedVariant;
      let price = this.price;

      if (variant && variant.price !== undefined) {
        price = variant.price;
      } else if (this.variants && this.variants.length > 0 && this.variants[0].price !== undefined) {
        price = this.variants[0].price;
      }

      if (variant?.compareAtPrice && !this.isBundleBuilderItem) {
        price = variant.compareAtPrice;
      } else if (this.variants[0]?.compareAtPrice && !this.isBundleBuilderItem) {
        price = this.variants[0].compareAtPrice;
      }

      const totalPrice = formatPrice(
        price,
        true,
        !!(this.isCartRecommendation || this.isGWP || this.inlineCard),
      );

      if (this.isBundleBuilderItem) {
        return this.$t('bundle_builder.price_from').replace(
          '{price}',
          totalPrice,
        );
      }

      return totalPrice;
    },
    formattedDiscountPrice() {
      const variant = this.getSelectedVariant;

      if (this.isBundleBuilderItem) {
        return false;
      }

      if (variant && variant.compareAtPrice) {
        return formatPrice(
          variant.price,
          true,
          !!(this.isCartRecommendation || this.isGWP || this.inlineCard),
        );
      }

      if (this.variants && this.variants.length > 0 && this.variants[0].compareAtPrice) {
        return formatPrice(
          this.variants[0].price,
          true,
          !!(this.isCartRecommendation || this.isGWP || this.inlineCard),
        );
      }

      return false;
    },
    bundleUnavailable() {
      return this.isBundleUnavailable();
    },
    ctaType() {
      const isOutOfStock = () =>
        (!this.available && this.product['is_discontinued']) || this.restrictedUnavailableProduct;

      const isNotify = () =>
        !this.available || (this.product.isRecent && !this.product.variants[0].available);

      const isPreorder = () =>
        this.tags?.includes('preorder');

      const isShopNow = () =>
        this.isDonateProduct || this.isGiftCard || this.hasMultiSwatch || this.isBundleBuilderItem;

      const isBundle = () =>
        this.isBundle;

      const isAddReco = () =>
        this.isCartRecommendation || this.isGWP;

      const isQuickShop = () =>
        (this.shadeCount > 1 && !this.disableQuickShop) || (this.sizeCount > 1 && !this.disableQuickShop);

      const isAdd = () =>
        this.shadeCount <= 1 && this.sizeCount <= 1;
      if (this.isVTO) return 'vto';
      if (isOutOfStock()) return 'out-of-stock';
      if (isNotify()) return 'notify';
      if (isPreorder()) return 'preorder';
      if (isShopNow()) return 'shop-now';
      if (isBundle()) return this.bundleUnavailable ? 'out-of-stock' : 'shop-now';
      if (isAddReco()) return 'add-reco';
      if (isQuickShop()) return 'quick-shop';
      if (isAdd()) return 'add';

      return 'shop-now';
    },
    firstOption() {
      return (this.productOptions || [])[0];
    },
    optionName() {
      return (this.optionNames || [])[0];
    },
    productOptions() {
      return this.optionsWithValues['Size'];
    },
    imageSrc() {
      let gwpImage = false;
      let gwpVariant;
      const variant = this.getSelectedVariant;

      if (variant && variant.image && !variant.image.src.includes('Liquid error')) {
        return variant.image.src;
      }

      if (this.isGWP) {
        gwpVariant = this.product.variants.find(
          (variant) => variant.isGWPVariant,
        );
      }

      if (gwpVariant) {
        gwpImage = this.currentShade
          ? this.currentShade.variants?.[0]?.image.src
          : gwpVariant.image.src;
        return gwpImage;
      }

      if (this.product.isRecent) {
        const recentImage = this.product.variants?.[0].image
          ? this.product.variants[0].image.src
          : this.featuredImage;
        if (image) {
          return image.src;
        }
        return recentImage;
      }

      if (this.customImage) return this.customImage;
      const key = `${this.optionName}-${(this.activeOption || '').replace(
        /[/ ]/g,
        '-',
      )}`.toLowerCase();
      const image = (this.media || []).find(({ src }) => {
        return src.indexOf(key) && src.indexOf('pos-2');
      });
      if (image) {
        return image.src;
      }
      return this.featuredImage;
    },
    productId() {
      return this.id;
    },
    hoverImageSrc() {
      if (this.customImage) return this.customImage;
      if (this.media && this.media[1]) return this.media[1].src;
      return this.imageSrc;
    },
    link() {
      return `${Shopify.routes.root}products/${this.handle}${this.activeVariantUrl}`;
    },
    activeVariantUrl() {
      const firstVariant = this.variants.find(({ title = '', id }) => {
        return ~title.indexOf(this.activeOption);
      });
      return firstVariant ? `?variant=${firstVariant.id}` : '';
    },
    activeVariantId() {
      return this.variants[0].id;
    },
    colorKey() {
      return this.optionNames.find((option) =>
        !!option ? option.toLowerCase() === 'color' : null,
      );
    },
    colorOptionIndex() {
      return this.optionNames.indexOf(this.colorKey);
    },
    sizeKey() {
      return this.optionNames.find((option) =>
        !!option ? option.toLowerCase() === 'size' : null,
      );
    },
    sizeOptionIndex() {
      return this.optionNames.indexOf(this.sizeKey);
    },
    swatches() {
      if (!this.colorKey) return false;

      return this.optionsWithValues[this.colorKey];
    },
    optionCombos() {
      return this.variants.map((variant) => {
        const combo = [];
        this.optionNames.forEach((option, i) => {
          combo[i] = slug(variant[`option${i + 1}`]);
        });
        return combo.join(' / ');
      });
    },
    badges() {
      if (!this.tags) {
        return;
      }
      const badges = this.tags
        .filter((tag) => {
          return tag.includes('badge|');
        })
        .slice(0, 2)
        .map((badge, index) => {
          const badgeText = badge.replace('badge|', '');
          if (this.isTablet) {
            const updatedBadge =
              badgeText.length > 20
                ? `${badgeText.slice(0, 15)}...`
                : badgeText;
            return updatedBadge;
          }
          return badgeText;
        });
      if (!badges.length) return false;

      return badges;
    },
    productAwards() {
      if (this.collectionAwards?.length) {
        return this.collectionAwards;
      }
      return this.awards;
    },
    hasPreorder() {
      if (this.tags) {
        return this.tags?.includes('preorder');
      }

      return false;
    },
    gwpBadgeText() {
      return this.$t('cart.gwp.badge');
    },
    sizeCount() {
      if (!this.product.isRecent && this.optionNames.includes('Size')) {
        return this.variants.length;
      }
      return 0;
    },
    shadeCountText() {
      return this.shadeCount && this.shadeCount > 1
        ? this.$t('products.card.shade_count', { count: this.shadeCount })
        : false;
    },
    sizeCountText() {
      return this.sizeCount && this.sizeCount > 1
        ? this.$t('products.card.size_count', { count: this.sizeCount })
        : false;
    },
    notifyText() {
      return this.$t('products.card.notify');
    },
    oosText() {
      return this.$t('products.card.out_of_stock');
    },
    quickShopText() {
      return this.isGWP || this.isRecommended || this.inlineCard
        ? 'Quick Shop'
        : this.$t('products.card.quick_shop');
    },
    vtoText() {
      return this.$t('products.card.vto');
    },
    shopNowText() {
      if (this.isDonateProduct) {
        return this.$t('products.card.donate_now');
      }
      if (this.isBundleBuilderItem) {
        return this.$t('products.card.customize');
      }
      return this.$t('products.card.shop_now');
    },
    preorderText() {
      return this.$t('products.card.preorder');
    },
    editText() {
      return this.$t('cart.gwp.edit');
    },
    removeText() {
      return this.$t('cart.gwp.remove');
    },
    viewDetailsText() {
      return this.$t('products.card.view_details');
    },
    selectText() {
      return this.$t('products.card.select');
    },
    selectedText() {
      return this.$t('products.card.selected');
    },
    changeYourShadeText() {
      return this.$t('products.card.change_your_shade');
    },
    addText() {
      return this.isGWP
        ? 'Add'
        : this.isBundleBuilderItem
        ? this.$t('products.card.customize')
        : this.$t('products.card.add');
    },
    selectShadeText() {
      return this.$t('quick_view.select_shade');
    },
    isSkinProduct() {
      return this.vendor === 'Fenty Skin';
    },
    isGWP() {
      return this.gwp === true;
    },
    isBundleBuilderItem() {
      return this.productObject.type === 'Bundle Builder';
    },
    GWPPromoCodes() {
      if (!Boolean(this.tags)) return [];
      return this.tags.reduce((arr, tag) => {
        const match = tag.match(/gwp offer\|(\w+)/i);
        if (match && match.length > 1) {
          return [...arr, match[1].toUpperCase()];
        }
        return arr;
      }, []);
    },
    thresholdReached() {
      if (this.gwpType === 'threshold' && this.GWPPromoCodes.length) {
        return !!this.gwpThresholds.find(
          (threshold) =>
            this.GWPPromoCodes.includes(threshold.code.toUpperCase()) &&
            this.totalCartGWPAmount >= threshold.value &&
            this.rewardUserList.length < threshold.quantity,
        );
      }
      if (this.gwpType === 'collection') {
        return true;
      }
      return false;
    },
    GWPOutOfStock() {
      return this.isGWP && !this.available;
    },
    replaceProductData() {
      const newProduct = this.replaceProducts.filter((p) => {
        return parseInt(p.id, 10) === parseInt(this.variants[0].id, 10);
      });
      return newProduct.length > 0 ? newProduct : false;
    },
    giftSelected() {
      const rule = this.rewardUserList.find((p) => {
        const id = p.id || p;
        return id === parseInt(this.variants[0].id);
      });
      return this.isGWP && rule;
    },
    showGWPBtn() {
      return this.isGWP ? this.rewardUserProductsLeft > 0 : true;
    },
    singleIsBundle() {
      return this.product.isBundle;
    },
    isSelectedInCustomSet() {
      if (this.customSelected[this.stepKey] === null) {
        return false;
      }

      return Number(this.customSelected[this.stepKey].id) === Number(this.id);
    },
    imageWidth() {
      if (!this.product.media) return '';
      return this.product.media?.length ? this.product.media[0].width : '350';
    },
    imageHeight() {
      if (!this.product.media) return '';
      return this.product.media?.length ? this.product.media[0].height : '420';
    },
    maximumText() {
      return this.$t('cart.label.maximum', {
        count: 5,
      });
    },
  },
  methods: {
    _initRivoMutationObserver() {
      const favoriteButton = this.$el.querySelector('rivo-favorite-button');
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            observer.disconnect();
            const svg = favoriteButton.querySelector('svg');
            if (svg) {
              const id = `add-to-favorite-${this.productId}`
              svg.setAttribute('role', 'img');
              svg.setAttribute('aria-labelledby', id);

              const title = document.createElement('title');
              title.setAttribute('id', id);
              title.textContent = 'Add to favorites';
              svg.prepend(title);
            }
          }
        });
      });
      observer.observe(favoriteButton, {
        childList: true,
        subtree: true,
      });
    },
    ...mapActions('global', [
      'addRemoveProductToList',
      'openModal',
      'closeModal',
      'updateBackInStock',
    ]),
    ...mapActions('cart', [
      'addToCart',
      'openCart',
      'closeCart',
      'addBundleItemsToCart',
    ]),
    ...mapActions('quickView', [
      'setProduct',
      'updateModalTitle',
      'updateCtaText',
      'setGWPModal',
      'updateItemToUpdate',
      'updateAction',
    ]),
    ...mapMutations('product', ['setCustomSelected']),
    handleShadeSelected(payload) {
      this.currentShade = payload;
      this.title = payload.productTitle;
      this.featuredImage = payload.image;
      this.media = payload.media;
      this.shadeSelected = true;
      this.handle = payload.handle;
      this.price = payload.price;
      if (payload.variants) {
        this.variants = payload.variants;
      }
      this.available = payload.available;

      this.optionNames = payload.optionNames;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'quickAddVariation',
        productVariation: payload.productVariation,
        productName: payload.productTitle,
      });
    },
    removeProduct() {
      this.addRemoveProductToList({
        id: parseInt(this.variants[0].id),
        productType: this.ctaType === 'quick-shop' ? 'common' : 'alt',
      });
    },
    onResize() {
      this.isTablet = isTablet();
    },
    async edit() {
      const storefrontResponse = await storefront.request(gqlQueries.product, {
        handle: this.handle,
      });
      const product = convertGQLToProductJson(storefrontResponse.product);

      this.updateAction('edit');
      this.updateItemToUpdate(this.id);
      this.updateModalTitle(this.$t('quick_view.edit_item_title'));
      this.updateCtaText(this.$t('quick_view.update_my_bag'));
      this.setProduct(product);
      this.openModal('quick-view');
    },
    buildOptionMap() {
      Object.keys(this.optionsWithValues).forEach((key, i) => {
        this.optionMap[key] = this.optionsWithValues[key].map((value) => {
          const combo = [];

          this.optionNames.forEach((option, j) => {
            combo[j] = i === j ? slug(value) : this.selectedColor;
          });

          return {
            label: value,
            value: slug(value),
            combo,
            enabled: this.optionCombos.includes(combo.join(' / ')),
          };
        });
      });
    },
    changeActiveOption(value) {
      this.activeOption = value;
    },
    openQuickShopModal() {
      if (this.isGWP) {
        this.setGWPModal(true);
      }
      let product = this.product;
      if (typeof this.product === 'string') {
        product = JSON.parse(decode(this.product));
      }

      product = {
        ...product,
        parentTitle: this.parentTitle,
        listType: this.listType,
        origin: 'quick shop',
        originTitle: this.productObject.title,
      };
      this.updateCtaText(this.$t('quick_view.add_to_bag'));
      this.setProduct(product);
      this.openModal('quick-view');
    },
    openInlineQuickView() {
      this.inlineQuickViewOpen = true;
    },
    closeInlineQuickView() {
      this.inlineQuickViewOpen = false;
    },
    slug(value) {
      return slug(value);
    },
    setColorOption(value) {
      this.selectedColor = slug(value);
      this.buildOptionMap();
    },
    trackSelectItem(isQuickShop = false) {
      const otherData = {
        origin: document.body.dataset.origin,
        originProduct: document.body.dataset.originProduct,
        index: this.cardIndex + 1,
      };

      const listData = {
        listName: htmlDecode(this.parentTitle) || undefined,
        navigationCategory: this.$parent._vnode.data?.attrs?.['data-link-title']
          ? this.$parent._vnode.data.attrs['data-link-title']
          : null,
        listId: this.listType,
        index: this.cardIndex + 1,
        ...(this.sectionName && { section: this.sectionName }),
        ...(isQuickShop && { click_text: 'quick shop' }),
        moduleName: this.listType?.replace('-', ' ')
      };

      setLocalStorageProperties(otherData, listData);

      let itemObject = [];

      if (this.isBundle) {
        otherData.bundleName = this.productObject.title;
        otherData.bundleId = this.productObject.id;
        listData.bundleName = this.productObject.title;
        listData.bundleId = this.productObject.id;
        itemObject = this.bundleProductsData.map((bundle) => {
          bundle.isBundle = true;
          bundle.tags = this.productObject.tags;
          return buildItemTrackingData(bundle, 1, otherData);
        });
      } else {
        itemObject = buildItemTrackingData(this.productObject, 1, otherData);
      }

      trackProductClick('select_item', itemObject, listData);
    },
    redirectToProductPage() {
      this.trackSelectItem();
      window.location = `${Shopify.routes.root}products/${this.handle}`;
    },
    redirectToProductPageWithVTO() {
      window.location = `${Shopify.routes.root}products/${this.handle}?try-shade=true`;
    },
    async trackClick() {
      this.trackSelectItem();

      if (this.isBundleBuilderItem) {
        trackEvent({
          event: 'select_bundle',
          site_section: window.__GLOBAL__.siteSection,
          component_name: 'Bundle Builder PLP Product Card',
          item_list_id: this.listType,
          item_list_name: this.parentTitle || undefined,
          bundle_type: 'bundle builder',
          bundle_name: htmlDecode(this.productObject.title),
          bundle_id: Number(this.productObject.id),
          price: this.productObject.price / 100,
          currency: window.Shopify.currency.active,
          index: this.cardIndex + 1,
        });
      }
      window.location = this.link;
    },
    getAddToCartType() {
      let addToCartType = 'Home';
      if (this.isRecommended) {
        addToCartType = 'Best Sellers';
      } else if (this.inlineQuickViewOpen) {
        addToCartType = 'Quick Add';
      } else {
        if (window.location.pathname.indexOf('/collections') > -1) {
          addToCartType = 'Product List Page';
          if (window.location.pathname.indexOf('/products') > -1) {
            addToCartType = 'Product Detail Page';
          }
        } else if (window.location.pathname.indexOf('/cart') > -1) {
          addToCartType = 'Cart';
        } else if (window.location.pathname.indexOf('/products') > -1) {
          addToCartType = 'Product Detail Page';
        } else if (window.location.pathname.indexOf('/pages') > -1) {
          addToCartType = 'Landing Page';
        } else if (window.location.pathname.indexOf('/account') > -1) {
          addToCartType = 'Account Page';
        }
      }

      return addToCartType;
    },
    addItemToCartFromReco(dataId) {
      this.addingToCart = false;
      const headerCartWrapper = select('.header-cart__wrapper');
      const firstItem = select(`[data-id="_${dataId}"]`);
      if (headerCartWrapper && firstItem) {
        headerCartWrapper.scrollTop = firstItem.offsetTop - 15;
      }
    },
    async addItemToCart() {
      const item = this.getSelectedVariant
      const callback = () => {
        this.isCartRecommendation || this.isGWP
          ? this.addItemToCartFromReco(item.id)
          : this.inlineCard
          ? this.closeModal()
          : setTimeout(() => {
              this.closeCart();
            }, 2500);
      };

      if (this.isCartRecommendation || this.isGWP) {
        this.addingToCart = true;
        setTimeout(() => {
          this.addingToCart = false;
        }, 2500);
      }

      const product = item?.product
        ? item.product
        : typeof this.product === 'string'
        ? JSON.parse(decode(this.product))
        : this.product;

      const properties = {
        _upsell_bundle:
          product.cart_upsell_bundle ||
          product.metafields?.general?.cart_upsell_bundle,
        _promo_text:
          product.promo_text || product.metafields?.cart_promo?.promo_text,
        _promo_link: product.promo_link || product.metafields?.cart_promo?.link,
        _product_collection: product.categories ? product.categories[0] : null,
        _product_tags: product.tags,
        _product_rating:
          product.reviewsAverage || product.metafields?.yotpo?.reviews_average,
        _item_list_name: this.parentTitle,
        _item_list_id: this.listType,
        _origin: document.body.dataset.origin,
        _origin_product: document.body.dataset.originProduct,
        _index: this.cardIndex + 1,
      };

      if (this.hasPreorder || product?.tags?.includes('preorder')) {
        properties._Preorder = 'true';
      }

      const sameProductsInCart = this.cart.items.filter(
        (cart_item) => Number(cart_item.id) === Number(item.id),
      );

      const sameIDProductsQuantity = sameProductsInCart?.reduce(
        (acc, rec) => (acc = acc + rec.quantity),
        0,
      );

      if (sameIDProductsQuantity < 5) {
        this.addToCart({
          id: item.id,
          quantity: item.quantity,
          callback,
          properties,
          moduleName: this.listType?.replace('-', ' ')
        });
      } else {
        this.limitReached = true;
        setTimeout(() => {
          this.limitReached = false;
        }, 2000);
      }

      let chosenVariant = [];

      if (this.inlineQuickViewOpen) {
        chosenVariant = [...chosenVariant, { item }];
      } else {
        chosenVariant = this.variants.filter(
          (variant) => variant.id === item.id,
        );
      }
      if (chosenVariant.length > 0) {
        const extraData = {
          addToCartType: this.getAddToCartType(),
          sku: chosenVariant[0].sku,
          variant: item.productVariation,
        };

        const productInfo = await addToCartEventData(
          product,
          item.quantity,
          extraData,
        );

        if (productInfo) {
          datalayer.dataLayerPush('addToCart', productInfo);
        }
      }
    },
    addItemToCartFromCTA(item) {
      this.addItemToCart(item);
    },
    validateAddToCart(id, quantity, sku) {
      const otherData = {
        reviews_average: this.product.reviewsAverage || '',
      };
      this.isGWP
        ? this.addRemoveProductToList({ id, otherData })
        : this.addItemToCart({ id, quantity, sku });
    },
    validateRedirect(id) {
      this.isGWP
        ? this.addRemoveProductToList({ id })
        : this.redirectToProductPage();
    },
    openNotifyModal() {
      this.updateBackInStock({
        product: {
          id: this.id,
          title: this.title,
          price: this.price,
          featuredImage: this.featuredImage,
        },
        variant: this.variants[0],
      });
      this.openModal('back-in-stock');
    },
    selectItemInCustomSet() {
      this.$emit('select-item', this.product);
    },
    initItemInCustomSet() {
      const customSelected = cloneDeep(this.customSelected);
      customSelected[this.stepKey] = null;
      this.setCustomSelected(customSelected);
    },
    async checkIsBundleAvailable() {
      const res = await checkBundleAvailability(this, storefront, gqlQueries);
      return res;
    },
    async checkIsProductAvailable() {
      const res = await checkProductAvailability(this, storefront, gqlQueries);
      return res;
    },
    onSizeSelected(selectedVariant) {
      this.currentVariantId = selectedVariant.id;

      this.trackSelectItem(false);
    },
  },
  template: `
    <div
      class="product-card relative"
      :class="[additionalClasses, {'selected': giftSelected}]"
    >
        <div class="relative">
          <div
            class="product-card__featured" tabindex="0">
            <div class="product-card__badges" v-if="!isGWP && !inlineCard">
              <span class="product-card__badge n1" :class="{ 'red': (!!badge && badge.toLowerCase() === 'sale') }" v-for="badge in badges" v-text="badge"></span>
            </div>
            <div class="product-card__awards" v-if="!isGWP && !inlineCard">
              <div class="product-card__award" v-for="award in productAwards">
                <div is="vue-image"
                  css="fit-cover"
                  :src="award.src"
                  :alt="award.alt"
                  :lazy="true"
                  :width="award.width?.toString()"
                  :height="award.height?.toString()">
                </div>
              </div>
            </div>
            <button :aria-label="'Go to ' + formattedTitle" ref="cardLink" class="product-card__featured-image" @click.prevent="trackClick">
              <div is="vue-image-v2"
                   class="fit-cover product-card__image"
                   :src="imageSrc"
                   sizes="(max-width: 375px) 190px, (max-width: 768px) 400px, (max-width: 1440px) 300px, 400px"
                   :alt="formattedTitle + ' view 1'"
                   width="372"
                   height="462"
                   loading="lazy"></div>
            </button>
            <button :aria-label="'Go to ' + formattedTitle" ref="cardLink" class="product-card__featured-image product-card__image--hover" @click.prevent="trackClick">
              <div is="vue-image-v2"
                   class="fit-cover"
                   :src="hoverImageSrc"
                   sizes="(max-width: 375px) 190px, (max-width: 768px) 400px, (max-width: 1440px) 300px, 400px"
                   :alt="formattedTitle + ' view 2'"
                   width="372"
                   height="462"
                   loading="lazy">
              </div>
            </button>
            <product-card-cta-actions
              v-if="!isTablet && !isGWP && !inlineCard"
              :cta-type="ctaType"
              :is-g-w-p="isGWP"
              :g-w-p-out-of-stock="GWPOutOfStock"
              :threshold-reached="thresholdReached"
              :gift-selected="giftSelected"
              :show-g-w-p-btn="showGWPBtn"
              :bundle-unavailable="bundleUnavailable"
              :handle="handle"
              :product="product"
              :variants="variants"
              :is-recommended="isRecommended"
              :remove-product="removeProduct"
              :is-bundle="isBundle"
              @add-item="addItemToCartFromCTA"
            >
            </product-card-cta-actions>
          </div>
          <div class="product-card__details">
            <div v-if="!isGWP && !inlineCard" class="product-card__badges">
              <span
                class="product-card__badge n1"
                :class="{ 'red': badge.toLowerCase() === 'sale' }"
                v-for="badge in badges"
                v-text="badge">
              </span>
            </div>
            <h3
              class="product-card__details-title p6 bold"
              :class="{'heading6': isGWP }"
              :id="'card-title-' + cardIndex"
              v-html="formattedTitle">
            </h3>

            <template v-if="isBundleBuilderItem">
              <p :id="'card-desc-' + cardIndex" v-if="product.cardDescription" class="product-card__details-card-desc light" v-html="product.cardDescription"></p>
            </template>

            <template v-if="isGWP">
              <div class="product-card__gwp-badge f2" v-text="gwpBadgeText"></div>
              <p :id="'card-desc-' + cardIndex" v-if="product.shortDescription" class="product-cart__short-description p" v-text="product.shortDescription"></p>
            </template>

            <div class="w1 f" v-if="finalTitle">
            <span
              v-if="!isGWP"
              class="product-card__details__variant b2 light"
              v-html="finalTitle.title"
            ></span>
            <span
              v-if="shadeCountText && !isGWP && !isBundle && !inlineCard"
              class="product-card__details__count b2 underline"
              v-html="shadeCountText">
            </span>
            <span
              v-if="sizeCountText && !isGWP && !isBundle && !inlineCard"
              class="product-card__details__count b2 underline"
              v-html="sizeCountText">
            </span>
            <span
              v-else
              class="product-card__details__variant p1"
              v-html="variantTitle"
            ></span>

          </div>

            <div class="product-card__reviews f" v-if="stars && productReviewsCount && !isGWP" :aria-label="starsAriaLabel">
              <div class="product-card__reviews__stars" v-html="stars" v-show="stars"></div>
              <span class="f2 product-card__reviews__count" v-html="reviewsText" v-show="stars"></span>
            </div>

            <product-card-price-details
              :is-g-w-p="isGWP"
              :formatted-price="formattedPrice"
              :formatted-discount-price="formattedDiscountPrice"
              :full-price-value-text="bundleFullPriceText"
              :full-price-value-text-h-t-m-l="bundleFullPriceHtml"
            >
            </product-card-price-details>
            <p v-show="limitReached" class="p2 red" v-text="maximumText"></p>
          </div>
        </div>

        <inline-quick-view
          v-if="isCartRecommendation && shadeCount > 1 || isGWP && shadeCount > 1 || inlineCard && shadeCount > 1"
          :inline-product="inlineProduct"
          :inline-quick-view-open="true"
          :inline-swatches="inlineSwatches"
          :is-gwp-collection-product="gwpCollectionProduct"
          :is-g-w-p="isGWP">
        </inline-quick-view>

        <div class="product-card__cta-primary" v-if="inCustomSet">
          <a :href="link"
            class="product-card__cta link product-card__cta--view-details"
            v-text="viewDetailsText">
          </a>
        </div>
        <product-card-cta-actions
          v-if="isTablet || isGWP || inlineCard"
          :cta-type="ctaType"
          :is-g-w-p="isGWP"
          :is-inline-card="inlineCard"
          :g-w-p-out-of-stock="GWPOutOfStock"
          :threshold-reached="thresholdReached"
          :gift-selected="giftSelected"
          :show-g-w-p-btn="showGWPBtn"
          :bundle-unavailable="bundleUnavailable"
          :handle="handle"
          :bundle-products-data="bundleProductsData"
          :product="product"
          :variants="variants"
          :is-recommended="isRecommended"
          :remove-product="removeProduct"
          :is-bundle="isBundle"
          @add-item="addItemToCartFromCTA"
          :shade-selected="shadeSelected"
          :current-shade="currentShade"
          :is-bundle-builder-item="isBundleBuilderItem"
          :current-variant-id="currentVariantId"
        >
        </product-card-cta-actions>
    </div>
    `,
};
